import { useId } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cn } from "./cn.js";

/**
 * The form primitives, and only the ones this product actually fills in.
 *
 * Everything an operator types here is a value the API already defines — a bitrate in
 * kbps, an interval in seconds, a tolerance in LU. So a field carries its unit beside
 * the control rather than inside the label, numbers are monospaced and right-aligned
 * so a column of them can be read down, and nothing invents a default: an untouched
 * field shows what the row holds.
 */

const controlClasses =
  "w-full min-h-11 rounded-sm border border-border bg-bg-2 px-3 text-sm text-ink transition-colors placeholder:text-ink-3 hover:border-border-hi focus:border-accent-line focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";

export function Label({
  children,
  htmlFor,
}: {
  children: ReactNode;
  htmlFor?: string | undefined;
}) {
  return (
    <label
      className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase"
      htmlFor={htmlFor}
    >
      {children}
    </label>
  );
}

export function Field({
  children,
  className,
  hint,
  htmlFor,
  label,
}: {
  children: ReactNode;
  className?: string | undefined;
  hint?: ReactNode;
  htmlFor?: string | undefined;
  label: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint ? (
        <p className="mt-1.5 text-xs leading-5 text-ink-3">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput({
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(controlClasses, className)} {...props} />;
}

/**
 * A number and its unit. The unit is part of the control because a bitrate without
 * "kbps" beside it is a number the operator has to remember the meaning of.
 */
export function NumberInput({
  className,
  unit,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { unit?: string }) {
  return (
    <div className="relative flex items-center">
      <input
        className={cn(
          controlClasses,
          "font-mono tabular-nums",
          unit && "pr-14",
          className,
        )}
        inputMode="decimal"
        type="number"
        {...props}
      />
      {unit ? (
        <span className="pointer-events-none absolute right-3 font-mono text-[0.6875rem] text-ink-3">
          {unit}
        </span>
      ) : null}
    </div>
  );
}

export function Select({
  children,
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(controlClasses, "pr-8", className)} {...props}>
      {children}
    </select>
  );
}

export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(controlClasses, "min-h-20 py-2.5 leading-6", className)}
      {...props}
    />
  );
}

/**
 * On or off, said as a switch rather than a checkbox because what it changes takes
 * effect the moment it is saved rather than being one answer among many on a form.
 */
export function Switch({
  checked,
  description,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  description?: string;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <button
        aria-checked={checked}
        aria-labelledby={id}
        className={cn(
          "relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50",
          checked
            ? "border-transparent bg-accent"
            : "border-border-hi bg-surface-hi",
        )}
        disabled={disabled}
        onClick={() => {
          onChange(!checked);
        }}
        role="switch"
        type="button"
      >
        <span
          aria-hidden="true"
          className={cn(
            "inline-block size-3.5 rounded-full transition-transform",
            checked
              ? "translate-x-[1.125rem] bg-accent-ink"
              : "translate-x-0.5 bg-ink-3",
          )}
        />
      </button>
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink" id={id}>
          {label}
        </div>
        {description ? (
          <p className="mt-0.5 text-xs leading-5 text-ink-3">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

/** A group of fields with one thing to say for itself. */
export function Fieldset({
  children,
  description,
  legend,
}: {
  children: ReactNode;
  description?: string;
  legend: string;
}) {
  return (
    <fieldset className="min-w-0 border-0 p-0">
      <legend className="text-sm font-semibold text-ink">{legend}</legend>
      {description ? (
        <p className="mt-1 max-w-2xl text-xs leading-5 text-ink-3">
          {description}
        </p>
      ) : null}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

/** Save, cancel, and what the form has to say for itself, in one row at the end. */
export function FormActions({
  children,
  note,
}: {
  children: ReactNode;
  note?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
      {children}
      {note ? (
        <span className="min-w-0 text-xs leading-5 text-ink-3">{note}</span>
      ) : null}
    </div>
  );
}
